@file:Suppress("DEPRECATION")
package org.example.newportfolio.components.widgets.animations

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.asList
import org.w3c.dom.events.Event

/**
 * Global animation driver. Renders nothing; wire it up exactly once
 * (typically inside `HomePage`).
 *
 * Three concerns, one composable:
 *
 *  1. **Scroll reveals.** One IntersectionObserver watches every element
 *     tagged with `.scroll-reveal`. When 12% of the element enters the
 *     viewport, it gets `.is-visible` (the CSS fades + slides in) and
 *     the observer stops watching it — reveals are one-shot, so we save
 *     event work after the page settles. We re-scan the DOM after a
 *     500 ms beat to pick up elements that mount late (Composables
 *     hydrating in stages).
 *
 *  2. **Cursor-following gradient blob.** A 480px blurred radial sits
 *     fixed-position and lerps toward the cursor on a rAF loop. The
 *     lerp coefficient (0.14) trails the cursor smoothly without
 *     feeling laggy. Hidden on touch devices via CSS media query.
 *
 *  3. **Scroll progress bar.** A 3px gradient at the top of the
 *     viewport scales horizontally with reading progress. Updates are
 *     rAF-throttled and the `transform: scaleX` never triggers layout.
 *
 * **Why one composable?** The three effects share the same browser
 * lifecycle hooks (DOM ready, animation frames, teardown). Bundling
 * them removes duplicated `DisposableEffect` plumbing and gives us one
 * place to enforce consistent perf rules (passive listeners, rAF
 * throttling, single observer disconnect on dispose).
 */
@Composable
fun ScrollAnimator() {
    DisposableEffect(Unit) {
        // ── 1. Scroll reveals via IntersectionObserver ────────────────
        // Local var for the JS-side observer reference. We need it
        // inside the callback (to `unobserve` the firing element) so it
        // can't just live inside `js("...")`.
        var observer: dynamic = null
        val revealHandler: (Array<dynamic>) -> Unit = { entries ->
            entries.forEach { entry ->
                if (entry.isIntersecting as Boolean) {
                    val target = entry.target as? HTMLElement
                    target?.classList?.add("is-visible")
                    target?.let { observer.unobserve(it) }
                }
            }
        }
        observer = js("new IntersectionObserver(revealHandler, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' })")

        // Initial scan
        fun observeAll() {
            document.querySelectorAll(".scroll-reveal:not(.is-visible)")
                .asList()
                .forEach { node -> (node as? Element)?.let { observer.observe(it) } }
        }
        observeAll()
        // Late-mount catch-up: Compose hydrates in waves; a single 500ms
        // re-scan reliably picks up grid items that finished rendering
        // after the first observer pass. Without this, the bottom rows
        // of the works grid would silently stay invisible.
        val lateScanId = window.setTimeout({ observeAll() }, 500)

        // ── 2. Cursor blob ────────────────────────────────────────────
        // Inject the blob element exactly once. Idempotent so dev hot-
        // reload doesn't stack duplicates.
        var blob = document.getElementById("cursor-blob") as? HTMLElement
        if (blob == null) {
            blob = (document.createElement("div") as HTMLElement).also {
                it.id = "cursor-blob"
                it.className = "cursor-blob"
                document.body?.appendChild(it)
            }
        }

        // Lerp state. Target = real cursor coords; current = render coords.
        // The gap creates a soft trailing motion.
        var targetX = window.innerWidth / 2.0
        var targetY = window.innerHeight / 2.0
        var currentX = targetX
        var currentY = targetY
        var blobActive = false
        var rafScheduled = false

        fun loop() {
            currentX += (targetX - currentX) * 0.14
            currentY += (targetY - currentY) * 0.14
            // translate3d forces the compositor path even on engines that
            // hesitate to promote 2D transforms.
            blob?.style?.transform = "translate3d(${currentX}px, ${currentY}px, 0)"
            // Continue ticking only while the cursor has moved recently.
            // Stops the rAF loop when idle to save battery.
            if (kotlin.math.abs(targetX - currentX) > 0.5 || kotlin.math.abs(targetY - currentY) > 0.5) {
                window.requestAnimationFrame { loop() }
            } else {
                rafScheduled = false
            }
        }

        val onMouseMove: (Event) -> Unit = { e ->
            val me = e.asDynamic()
            targetX = (me.clientX as Number).toDouble()
            targetY = (me.clientY as Number).toDouble()
            if (!blobActive) {
                blob?.classList?.add("is-active")
                blobActive = true
            }
            if (!rafScheduled) {
                rafScheduled = true
                window.requestAnimationFrame { loop() }
            }
        }
        // passive: true — we never preventDefault on mousemove, so the
        // browser can skip the "did this handler cancel?" check.
        document.addEventListener("mousemove", onMouseMove, js("{ passive: true }"))

        // ── 3. Scroll progress bar ────────────────────────────────────
        var bar = document.getElementById("scroll-progress") as? HTMLElement
        if (bar == null) {
            bar = (document.createElement("div") as HTMLElement).also {
                it.id = "scroll-progress"
                it.className = "scroll-progress"
                document.body?.appendChild(it)
            }
        }

        var progressTicking = false
        val onScroll: (Event) -> Unit = { _ ->
            if (!progressTicking) {
                progressTicking = true
                window.requestAnimationFrame {
                    val scrollable = (document.documentElement?.scrollHeight ?: 0) - window.innerHeight
                    val ratio = if (scrollable > 0) {
                        (window.scrollY / scrollable).coerceIn(0.0, 1.0)
                    } else 0.0
                    bar?.style?.transform = "scaleX($ratio)"
                    progressTicking = false
                }
            }
        }
        window.addEventListener("scroll", onScroll, js("{ passive: true }"))
        // Fire once to set the initial state (the user might land mid-page
        // via a hash anchor like `#contact`).
        onScroll(js("new Event('scroll')").unsafeCast<Event>())

        onDispose {
            observer.disconnect()
            window.clearTimeout(lateScanId)
            document.removeEventListener("mousemove", onMouseMove)
            window.removeEventListener("scroll", onScroll)
            // Leave the DOM nodes in place — DisposableEffect runs on
            // composition exit, but the page is gone at that point too.
        }
    }
}
